import React, {CSSProperties, useState} from "react"
import Icon from "../Icon/Icon"
import {b64DecodeArrayBuffer} from "../../utilities/helpers"
import Skeleton from "react-loading-skeleton"
import {useModal} from "react-modal-hook"
import checkFrontNoCheck from "../../images/check-front-no-check.svg"
import checkBackNoCheck from "../../images/check-back-no-check.svg"
import {BackCheckImageModal, FrontCheckImageModal} from "./CheckImageModal"
import classNames from "classnames"
import {ImageTransformControls} from "../ImageTransformControls/ImageTransformControls"
import {useToolbar} from "../../hooks/useToolbar"
import {ApplicationDocumentSide} from "../../resources/application"

interface CheckImageCardProps {
	frontImage?: ArrayBuffer | null
	backImage?: ArrayBuffer | null
	id: string
	status: string
	originated?: boolean
	compact?: boolean
}

export function CheckImageCard({frontImage, backImage, id, status, originated, compact = false}: CheckImageCardProps) {
	enum Side {
		front = "Front side",
		back = "Back side",
	}

	const frontImageLink = frontImage ? `data:image/png;base64, ${b64DecodeArrayBuffer(frontImage)}` : ""
	const backImageLink = backImage ? `data:image/png;base64,  ${b64DecodeArrayBuffer(backImage)}` : ""
	const [side, setSide] = useState(Side.front)
	const {zoom, rotation} = useToolbar(ApplicationDocumentSide.Front)
	const inlineImgStyle = {
		"--img-rotation": rotation.val + "deg",
		"--img-scale": zoom.val,
		"--img-container-align": rotation.val === 90 || rotation.val === 270 ? "center" : "start",
	} as CSSProperties

	const changeSide = () => {
		if (side === Side.front) {
			setSide(Side.back)
		} else {
			setSide(Side.front)
		}
	}

	let frontImageObject: JSX.Element, backImageObject: JSX.Element

	switch (backImage) {
		case undefined:
			backImageObject = (
				<div className={"skeleton-placeholder"}>
					<Skeleton />
				</div>
			)
			break
		case null:
			backImageObject =
				side == Side.front ? (
					<img style={inlineImgStyle} className="check-front-image" src={checkBackNoCheck} alt={Side.front} />
				) : (
					<div className="no-check-placeholder">
						<p>
							{" "}
							{originated
								? "Images will be available once the check is deposited"
								: "There is no back image to show."}{" "}
						</p>{" "}
						<br /> <img style={inlineImgStyle} className="check-front-image" src={checkBackNoCheck} alt={Side.front} />
					</div>
				)
			break
		default:
			backImageObject = <img style={inlineImgStyle} className="check-back-image" src={backImageLink} alt={Side.back} />
	}

	switch (frontImage) {
		case undefined:
			frontImageObject = (
				<div className={"skeleton-placeholder"}>
					<Skeleton />
				</div>
			)
			break
		case null:
			frontImageObject =
				side == Side.back ? (
					<img style={inlineImgStyle} className="check-front-image" src={checkFrontNoCheck} alt={Side.front} />
				) : (
					<div className="no-check-placeholder">
						<p>
							{" "}
							{originated
								? "Images will be available once the check is deposited"
								: "There is no front image to show."}{" "}
						</p>{" "}
						<br /> <img style={inlineImgStyle} className="check-front-image" src={checkFrontNoCheck} alt={Side.front} />
					</div>
				)
			break
		default:
			frontImageObject = (
				<img style={inlineImgStyle} className="check-front-image" src={frontImageLink} alt={Side.front} />
			)
	}

	const [showFrontImage, hideFrontImage] = useModal(
		() => (
			<FrontCheckImageModal
				id={id}
				status={status}
				frontImage={frontImageObject}
				backImage={backImageObject}
				closeModal={hideFrontImage}
			/>
		),
		[frontImageObject]
	)

	const [showBackImage, hideBackImage] = useModal(
		() => (
			<BackCheckImageModal
				id={id}
				status={status}
				frontImage={frontImageObject}
				backImage={backImageObject}
				closeModal={hideBackImage}
			/>
		),
		[backImageObject]
	)

	const showImage = () => {
		if (side === Side.front && frontImage) {
			showFrontImage()
		}

		if (side === Side.back && backImage) {
			showBackImage()
		}
	}

	return (
		<div className={classNames("check-image-card", {compact})}>
			<header>
				<a
					className="single-check-deposit-link-with-icon"
					target="_blank"
					rel="noreferer"
					href=""
					onClick={(e) => e.preventDefault()}
				>
					<span onClick={showImage}>{side}</span>
					<Icon icon="maximize-square-2--interface-essential" size={13} style={{position: "relative", top: "2px"}} />
				</a>

				{((side === Side.front && frontImage) || (side === Side.back && backImage)) && (
					<ImageTransformControls
						changeRotation={rotation.changeRotation}
						zoomIn={zoom.zoomIn}
						zoomOut={zoom.zoomOut}
						zoomVal={zoom.val}
					/>
				)}
			</header>

			<div
				style={inlineImgStyle}
				className="check-image-card-container"
				data-tracking-label="open-check-image-in-full-screen"
				onClick={showImage}
			>
				{side === Side.front ? frontImageObject : backImageObject}
			</div>

			<div className="check-image-card-preview-container" onClick={changeSide}>
				<span>{side === Side.front ? Side.back : Side.front}</span>
				<div className="check-image-card-preview-container-wrapper">
					<div className="check-image-card-preview-container-mask" />
					{side === Side.front ? backImageObject : frontImageObject}
				</div>
			</div>
		</div>
	)
}
